import { fetchTableItems } from '.';
import { TableItemsOptions, ProgramListType } from '../../types';
import axios from 'axios';
import {
	CreateProgramDto,
	Program,
	ProgramEvent,
	ProgramEventCreateDto,
	ProgramFunderReport,
	ProgramFunderReportCreateDto,
	ProgramReport,
	ProgramReportUpdateDto,
	ProgramValidations,
	ReportType,
	TagType,
	UpdateProgramDto,
	ProposalCreateDto,
	ProgramFormValidations,
	ProgramFormData,
	ProgramFormStatusCode,
	Proposal,
	ProposalUpdateDto,
	ProgramEventUpdateDto,
	ProgramFormTemplate,
	ApprovalActionDto,
	AdvancePaymentRequest,
	AdvancePaymentRequestCreateDto,
	AdvancePaymentRequestUpdateDto,
	Evaluation,
	EvaluationCreateDto,
	EvaluationUpdateDto,
	QuantitativeEvaluationField,
	Funder,
} from '../../types/program';
import { compressJsonToString } from '@/utils';

export const programApi = {
	async fetchPrograms(
		options: TableItemsOptions,
		listType: ProgramListType,
	): Promise<{ result: Program[]; total: number }> {
		const baseUrl = '/program/programs';
		const params = new URLSearchParams();

		params.append('status', listType);

		// build URL
		const data = await fetchTableItems({
			...options,
			url: `${baseUrl}?${params.toString()}`,
		});
		return data;
	},

	async fetchFunders(): Promise<Funder[]> {
		const { data } = await axios.get(`/program/funders`);
		return data;
	},

	async fetchProgramReports(
		options: TableItemsOptions,
		programId: string,
	): Promise<{ result: ProgramReport[]; total: number }> {
		return await fetchTableItems({
			...options,
			url: `/program/program-reports/?programId=${programId}`,
		});
	},

	async fetchProgramEvents(
		options: TableItemsOptions,
		reportId?: string,
		proposalId?: string,
	): Promise<{ result: ProgramEvent[]; total: number }> {
		let url = `/program/program-events?`;
		const params = [];
		if (reportId) {
			params.push(`reportId=${reportId}`);
		}
		if (proposalId) {
			params.push(`proposalId=${proposalId}`);
		}
		url += params.join('&');
		return await fetchTableItems({
			...options,
			url,
		});
	},

	async createProgramEvents(
		programEvents: ProgramEventCreateDto[],
	): Promise<ProgramEvent[]> {
		const { data } = await axios.post(`/program/program-events`, programEvents);
		return data;
	},

	async updateProgramEvent({
		eventId,
		eventTags,
		proposalId,
	}: ProgramEventUpdateDto): Promise<ProgramEvent> {
		const { data } = await axios.put(`/program/program-events/${eventId}`, {
			eventTags,
			proposalId,
		});
		return data;
	},

	async fetchProgramReport(id: string): Promise<ProgramReport> {
		const { data } = await axios.get(`/program/program-reports/${id}`);
		return data;
	},

	async fetchProgramById(id: string): Promise<Program> {
		const { data } = await axios.get(`/program/programs/${id}`);
		return data;
	},

	async fetchAllTagTypes(): Promise<TagType[]> {
		const { data } = await axios.get('/program/tag-types');
		return data;
	},

	async fetchReportTypeTagTypes(reportTypeId: string): Promise<TagType[]> {
		const { data } = await axios.get('/program/tag-types', {
			params: { reportTypeId },
		});
		return data;
	},

	async getReportTemplateSignedUrl(
		id: string,
	): Promise<{ url: string; filename: string }> {
		const { data } = await axios.get(
			`/program/report-types/${id}/request-read-signed-url`,
		);
		return data;
	},

	async fetchProgramFunderReports(
		programReportId: string,
	): Promise<ProgramFunderReport[]> {
		const { data } = await axios.get('/program/program-funder-reports', {
			params: { programReportId },
		});
		return [...data];
	},

	async requestContactFileSignedUrl(body: any): Promise<any> {
		const { data } = await axios.post(
			'/program/program-funder-reports/request-upload-signed-url',
			body,
		);
		return data;
	},

	async createProgramFunderReport(dto: ProgramFunderReportCreateDto) {
		const { data } = await axios.post('/program/program-funder-reports', dto);
		return data;
	},

	async getFunderReportSignedUrl(id: string): Promise<string> {
		const { data } = await axios.get(
			`/program/program-funder-reports/${id}/request-read-signed-url`,
		);
		return data;
	},

	async createProgram(
		program: Program | CreateProgramDto,
		validations: ProgramValidations,
	) {
		const postProgram: CreateProgramDto = {
			status: program.status,
			ngoId: program.ngoId,
			title: program.title,
			description: program.description,
			programStart: program.programStart,
			programEnd: program.programEnd,
			ownerCenterIds: program.ownerCenterIds,
			picUserId: program.picUserId,
			picName: program.picName,
			picJobTitle: program.picJobTitle,
			picEmail: program.picEmail,
			picPhoneNumber: program.picPhoneNumber,

			funderId: program.funderId,
			fundingId: program.fundingId,
			fundingCurrency: program.fundingCurrency,
			fundingAmount: program.fundingAmount,
			reportNamePrefix: program.reportNamePrefix,
			memberships: program.memberships,

			targetSubDistricts: program.targetSubDistricts,
			notes: program.notes,

			proposalTemplateId: program.proposalTemplateId,
			advancePaymentRequestTemplateId: program.advancePaymentRequestTemplateId,
			evaluationTemplateId: program.evaluationTemplateId,
			reimbursementRequestTemplateId: program.reimbursementRequestTemplateId,
			approvalLevel: program.approvalLevel,
			approversLevel1: program.approversLevel1,
			approversLevel2: program.approversLevel2,
			approversLevel3: program.approversLevel3,
			approversFinancial: program.approversFinancial,
			approvalAmountLimit: program.approvalAmountLimit,

			enablers: program.enablers,

			validations: validations,
		};
		const { data } = await axios.post(`/program/programs`, postProgram);
		return data;
	},

	async updateProgram(
		newProgram: Program | CreateProgramDto,
		validations: ProgramValidations,
	) {
		// Guard
		const program = newProgram as Program;
		if (!program) throw new Error("Can't update with CreateProgramDto");

		if (!newProgram.id) throw new Error('Missing program.id');
		if (typeof newProgram.fundingAmount == 'string') {
			newProgram.fundingAmount = parseInt(newProgram.fundingAmount);
		}

		const postProgram: UpdateProgramDto = {
			status: program.status,
			ngoId: program.ngoId,
			title: program.title,
			description: program.description,
			programStart: program.programStart,
			programEnd: program.programEnd,
			ownerCenterIds: program.ownerCenterIds,
			picUserId: program.picUserId,
			picName: program.picName,
			picJobTitle: program.picJobTitle,
			picEmail: program.picEmail,
			picPhoneNumber: program.picPhoneNumber,

			funderId: program.funderId,
			fundingId: program.fundingId,
			fundingCurrency: program.fundingCurrency,
			fundingAmount: program.fundingAmount,
			reportNamePrefix: program.reportNamePrefix,
			memberships: program.memberships,

			targetSubDistricts: program.targetSubDistricts,
			notes: program.notes,

			proposalTemplateId: program.proposalTemplateId,
			advancePaymentRequestTemplateId: program.advancePaymentRequestTemplateId,
			evaluationTemplateId: program.evaluationTemplateId,
			reimbursementRequestTemplateId: program.reimbursementRequestTemplateId,
			approvalLevel: program.approvalLevel,
			approversLevel1: program.approversLevel1,
			approversLevel2: program.approversLevel2,
			approversLevel3: program.approversLevel3,
			approversFinancial: program.approversFinancial,
			approvalAmountLimit: program.approvalAmountLimit,

			enablers: program.enablers,

			validations: validations,
		};
		const { data } = await axios.put(
			`/program/programs/${program.id}`,
			postProgram,
		);
		return data;
	},

	async deleteProgramEvent(id: string): Promise<ProgramEvent> {
		const { data } = await axios.delete(`/program/program-events/${id}`);
		return data;
	},

	async fetchProgramReportRevisions(reportId: string): Promise<ReportType[]> {
		const { data } = await axios.get(
			`/program/program-reports/${reportId}/revisions`,
		);
		return data;
	},

	async updateProgramReport(
		reportId: string,
		dto: ProgramReportUpdateDto,
	): Promise<ProgramReport> {
		const { data } = await axios.put(
			`/program/program-reports/${reportId}`,
			dto,
		);
		return data;
	},

	async downloadReportData({
		id,
		programEventIds,
		callback,
	}: {
		id: string;
		programEventIds?: string[];
		callback?: Function;
	}): Promise<{ files: { name: string; file: string }[]; forceZip?: boolean }> {
		const { data } = await axios.post(
			`/program/program-reports/${id}/generate`,
			{ programEventIds },
		);
		const downloadId = data.downloadId;
		let report: any;
		while (!report?.data.files?.length) {
			await new Promise(r => setTimeout(r, 1000));
			report = await axios.get(`/program/program-reports/${id}/download`, {
				params: { downloadId },
			});
			if (callback) {
				callback(report?.data?.progress);
				await new Promise(r => setTimeout(r, 1000));
			}
		}
		return report.data;
	},

	async fetchProgramFormTemplates(): Promise<ProgramFormTemplate> {
		const { data } = await axios.get(`/program/program-form-templates`);
		return data;
	},

	async fetchProgramFormTemplatesCombinations(): Promise<
		ProgramFormTemplate[]
	> {
		const { data } = await axios.get(
			`/program/program-form-template-combinations`,
		);
		return data;
	},

	//------------ Proposal ------------------------

	async fetchProgramProposalsAndEvaluations(
		options: TableItemsOptions,
		programId: string,
		statuses?: ProgramFormStatusCode[],
	): Promise<{ result: Proposal[]; total: number }> {
		const data = await fetchTableItems({
			...options,
			url: `/program/proposals?programId=${programId}${
				statuses
					? '&' + statuses.map(status => `statuses[]=${status}`).join('&')
					: ''
			}`,
		});
		return data;
	},

	async fetchProposalById(id: string): Promise<Proposal> {
		const { data } = await axios.get(`/program/proposals/${id}`);
		return data;
	},

	async fetchProposalSubscriptionFees(id: string): Promise<any> {
		const { data } = await axios.get(
			`/program/proposals/${id}/subscription-fees`,
		);
		return data;
	},

	async createProposal(
		programId: string,
		proposalTemplateId: string,
		status: ProgramFormStatusCode,
		proposal: ProgramFormData,
		requiredApprovalLevel: number,
		validations: ProgramFormValidations,
	) {
		const postProposal: ProposalCreateDto = {
			programId,
			proposalTemplateId,
			status,
			proposalData: proposal,
			requiredApprovalLevel: requiredApprovalLevel,
			validations: validations,
		};
		const { data } = await axios.post(`/program/proposals`, postProposal);
		return data;
	},

	async updateProposal(
		id: string,
		programId: string,
		status: ProgramFormStatusCode,
		proposalData: ProgramFormData,
		requiredApprovalLevel: number,
		validations: ProgramFormValidations,
	) {
		const postProposal: ProposalUpdateDto = {
			programId,
			status,
			proposalData,
			validations: validations,
			requiredApprovalLevel,
		};
		const { data } = await axios.put(`/program/proposals/${id}`, postProposal);
		return data;
	},

	async updateProgramFormApproval(
		id: string,
		formType: string,
		action: ApprovalActionDto,
	) {
		const { data } = await axios.post(
			`/program/${formType}/${id}/approval`,
			action,
		);
		return data;
	},

	//------------ AdvancePaymentRequest ------------------------

	async fetchAdvancePaymentRequests(
		options: TableItemsOptions,
		proposalId: string,
		programId: string,
	): Promise<{ result: AdvancePaymentRequest[]; total: number }> {
		const data = await fetchTableItems({
			...options,
			url: `/program/advance-payment-requests?programId=${programId}&proposalId=${proposalId}`,
		});
		return data;
	},

	async fetchAdvancePaymentRequestById(
		id: string,
	): Promise<AdvancePaymentRequest> {
		const { data } = await axios.get(`/program/advance-payment-requests/${id}`);
		return data;
	},

	async createAdvancePaymentRequest(
		proposalId: string,
		advancePaymentRequestTemplateId: string,
		status: ProgramFormStatusCode,
		advancePaymentRequest: ProgramFormData,
		requiredApprovalLevel: number,
		validations: ProgramFormValidations,
	) {
		const postAdvancePaymentRequest: AdvancePaymentRequestCreateDto = {
			proposalId,
			advancePaymentRequestTemplateId,
			status,
			advancePaymentRequestData: advancePaymentRequest,
			requiredApprovalLevel: requiredApprovalLevel,
			validations: validations,
		};
		const { data } = await axios.post(
			`/program/advance-payment-requests`,
			postAdvancePaymentRequest,
		);
		return data;
	},

	async updateAdvancePaymentRequest(
		id: string,
		proposalId: string,
		status: ProgramFormStatusCode,
		advancePaymentRequestData: ProgramFormData,
		requiredApprovalLevel: number,
		validations: ProgramFormValidations,
	) {
		const postAdvancePaymentRequest: AdvancePaymentRequestUpdateDto = {
			proposalId,
			status,
			advancePaymentRequestData,
			validations: validations,
			requiredApprovalLevel,
		};
		const { data } = await axios.put(
			`/program/advance-payment-requests/${id}`,
			postAdvancePaymentRequest,
		);
		return data;
	},

	async downloadAdvancePaymentRequestReport(id: string) {
		const { data } = await axios.get(
			`/program/advance-payment-requests/${id}/pdf-report`,
		);
		return data;
	},

	//------------ Evaluation ------------------------

	async fetchQuantitativeEvaluation(
		id: string,
		fields: QuantitativeEvaluationField[],
	): Promise<Evaluation> {
		const { data } = await axios.post(
			`/program/proposals/${id}/quantitative-evaluation`,
			{
				fields,
			},
		);
		return data;
	},

	async fetchEvaluationById(id: string): Promise<Evaluation> {
		const { data } = await axios.get(`/program/proposals/${id}`);
		return data;
	},

	async createEvaluation(
		proposalId: string,
		evaluationTemplateId: string,
		reimbursementTemplateId: string,
		status: ProgramFormStatusCode,
		evaluationData: ProgramFormData,
		requiredApprovalLevel: number,
		validations: ProgramFormValidations,
	) {
		const evaluationDataCompressed = compressJsonToString(evaluationData);
		const postEvaluation: EvaluationCreateDto = {
			proposalId,
			evaluationTemplateId,
			reimbursementTemplateId,
			status,
			evaluationDataCompressed,
			requiredApprovalLevel,
			validations,
		};
		const { data } = await axios.post(`/program/evaluations`, postEvaluation);
		return data;
	},

	async updateEvaluation(
		id: string,
		proposalId: string,
		status: ProgramFormStatusCode,
		evaluationData: ProgramFormData,
		requiredApprovalLevel: number,
		validations: ProgramFormValidations,
	) {
		const evaluationDataCompressed = compressJsonToString(evaluationData);

		const postEvaluation: EvaluationUpdateDto = {
			proposalId,
			status,
			evaluationDataCompressed,
			validations: validations,
			requiredApprovalLevel,
		};
		const { data } = await axios.put(
			`/program/evaluations/${id}`,
			postEvaluation,
		);
		return data;
	},

	async notifyEndorsements(
		proposalId: string,
		endorsementIds: string[],
		confirm?: boolean,
	): Promise<any> {
		const { data } = await axios.post(
			`/program/proposals/${proposalId}/notify-enablers`,
			{
				endorsementIds,
				confirm,
			},
		);
		return data;
	},

	async endorseProposal(id: string) {
		const { data } = await axios.post(`/program/endorsements/${id}/endorse`);
		return data;
	},

	async rejectProposal(id: string) {
		const { data } = await axios.post(`/program/endorsements/${id}/reject`);
		return data;
	},

	async downloadReimbursementReport(evaluationId: string, proposalId: string) {
		const { data } = await axios.get(
			`/program/evaluations/${evaluationId}/pdf-report`,
			{
				params: { proposalId },
			},
		);
		return data;
	},

	async downloadReimbursementTransactionProof(evaluationId: string) {
		const { data } = await axios.get(
			`/program/evaluations/${evaluationId}/proof-transactions-report`,
			{},
		);
		return data;
	},
};
